import ConnectionStatus from '@app/types/connection-status.type';
import { TranslatableKey } from '@app/types/translatable.type';
import { ModelMixin } from '../core/base-generic.model';

export type ImportIntegrationCategory = 'IDP' | 'PAYROLL' | 'HR' | 'PERFORMANCE' | 'ATS' | 'BENEFITS' | 'ACCOUNTING';

type AdditionalDataFields = { [key: string]: unknown } & {
    externalIdentifier: string;
    logoUrl: string;
    integrationUrl: string;
    documentationUrl: string;
    tokenInstructions: string[];
};

export class ImportIntegration extends ModelMixin<ImportIntegration>() {
    protected static _type = 'integration';

    protected static _resource = 'integrations';

    protected static _version = 'v2';

    get name(): string {
        return this.additionalData.externalIdentifier.toUpperCase();
    }

    get logo(): string {
        return this.additionalData?.logoUrl ?? this.assetBaseURL + this.name + '.png';
    }

    get primaryCategory(): ImportIntegrationCategory {
        return this._attributes['primaryCategory'].toUpperCase();
    }

    // The BE returns much more data than we need in terms of category, we only care about the name
    get categories(): ImportIntegrationCategory[] {
        const categories: Category[] = this._attributes['categories'];
        return categories.map((category) => category.name?.toUpperCase() as ImportIntegrationCategory);
    }

    get status(): ConnectionStatus {
        return this._attributes['status']?.toLowerCase() ?? 'disconnected';
    }

    set status(status: ConnectionStatus) {
        this._attributes['status'] = status.toLowerCase();
    }

    // This has to be named description to display correctly on the integration card
    get description(): TranslatableKey {
        return `import.integrations.categories.${this.primaryCategory}.label.default`;
    }

    // asset baseURL is used as a fallback in case the logoURL isn't provided
    get assetBaseURL(): string {
        return 'https://api.unified.to/docs/images/';
    }

    get integrationURL(): string {
        return this.additionalData.integrationUrl;
    }

    get documentationURL(): string {
        return this.additionalData.documentationUrl;
    }

    get tokenInstructions(): string[] {
        return this.additionalData.tokenInstructions;
    }

    private get additionalData(): AdditionalDataFields {
        return this._attributes['additionalData'];
    }
}

class Category extends ModelMixin<Category>() {
    protected static _type = 'category';

    get name(): string {
        return this._attributes['name'].toUpperCase();
    }

    get createdAt(): string {
        return this._attributes['created_at'];
    }

    get updatedAt(): string {
        return this._attributes['updated_at'];
    }

    get description(): string {
        return this._attributes['description'];
    }
}
